'use client';

import type { LogoutOptions } from '@auth0/auth0-react';
import { Auth0Context, initialContext } from '@auth0/auth0-react';
import { useCallback, useContext } from 'react';

import {
    clearAuth0ClaimsRefreshState,
    clearAutoshipDataCache,
    clearChatData,
    clearFavorites,
    clearLoyaltyData,
    markAuth0LogoutInFlight,
    NAVIGATION_URLS,
    removeAuthTokens,
    removeOrderGrooveToken,
    SESSION_EVALUATED_DOM_EVENT,
} from '@libs/core';

import { UserSessionProviderContext } from '../providers/UserSessionProvider';
import { getAuth0ReturnTo } from '../utils/getAuth0ReturnTo';

export type UseAuth0LogoutOptions = {
    callback?: () => void;
    returnTo?: {
        path: string;
        query?: string;
    };
};

export type UseAuth0LogoutConfig = {
    /** When true, logout sends the user to sign-in with `prompt=login` instead of the site origin. */
    forceReauth?: boolean;
};

export type Auth0LogoutFn = (options?: UseAuth0LogoutOptions) => Promise<void>;

type ForceLogoutDependencies = {
    logout: (options?: LogoutOptions) => Promise<void>;
    setUser?: (user: undefined) => void;
};

function getSignInUrl(returnTo?: UseAuth0LogoutOptions['returnTo']): string {
    const signInUrl = new URL(NAVIGATION_URLS.velocity.signIn, globalThis.location.origin);

    if (returnTo) {
        signInUrl.searchParams.set('returnTo', getAuth0ReturnTo({ pathname: returnTo.path, search: returnTo.query }));
    }

    signInUrl.searchParams.set('prompt', 'login');

    return signInUrl.toString();
}

async function logoutAndClearSession(
    { callback }: UseAuth0LogoutOptions,
    { logout, setUser }: ForceLogoutDependencies,
    returnTo: string
): Promise<void> {
    setUser?.(undefined);
    // Do not stamp `auth0Reauth` — that cookie is for inactivity, and leaving it
    // set would block silent SSO after a later SBW login. The in-memory flag blocks
    // this document; the shared logout generation blocks other tabs' in-flight writes.
    markAuth0LogoutInFlight();
    removeAuthTokens();
    globalThis.window?.dispatchEvent(new CustomEvent(SESSION_EVALUATED_DOM_EVENT));
    removeOrderGrooveToken();

    clearFavorites();
    clearLoyaltyData();
    clearAuth0ClaimsRefreshState();
    clearAutoshipDataCache();
    clearChatData();

    callback?.();

    await logout({
        logoutParams: {
            returnTo,
        },
    });
}

/**
 * Clears local user state, ends the Auth0 session, and sends the user to sign in.
 * When provided, `returnTo` is preserved through sign in.
 */
export async function forceLogout(
    options: UseAuth0LogoutOptions = {},
    dependencies: ForceLogoutDependencies
): Promise<void> {
    await logoutAndClearSession(options, dependencies, getSignInUrl(options.returnTo));
}

export function useAuth0Logout({ forceReauth = false }: UseAuth0LogoutConfig = {}): Auth0LogoutFn {
    const { logout } = useContext(Auth0Context);
    const userSessionContext = useContext(UserSessionProviderContext);
    const hasProvider = logout !== initialContext.logout;
    const setUser = userSessionContext?.setUser;

    return useCallback(
        async (options: UseAuth0LogoutOptions = {}) => {
            if (!hasProvider) {
                return;
            }

            const dependencies = { logout, setUser };

            if (forceReauth) {
                await forceLogout(options, dependencies);
                return;
            }

            await logoutAndClearSession(options, dependencies, globalThis.location.origin);
        },
        [forceReauth, hasProvider, logout, setUser]
    );
}
